import React, { useEffect, useState } from "react";
import {
  Button,
  FormLabel,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  updateData,
} from "../../actions/actionItems";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import { SpaPropertyEnum } from "../../../constants/NavbarList";
import { isDuplicate } from "../../../constants/helper";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import {
  validateString,
  validationLength,
  validationMessages,
  validationType,
} from "../../actions/validations";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import {
  departmentsTooltip,
  golfTooltipHeaders,
  spaTooltipHeaders,
} from "../../../constants/TooltipInformation";
import AlertComponent from "../../subcomponents/AlertComponent";

const SpaServiceEquipment = ({
  data,
  fieldValues,
}: {
  data: (value: any) => void;
  fieldValues: any;
}) => {
  const [equipmentCode, setEquipmentCode] = useState<string | null>(null);
  const [equipmentName, setEquipmentName] = useState<string | null>(null);
  const [quantity, setQuantity] = useState<number | "">("");
  const [codeFieldError, setCodeFieldError] = useState<boolean>(false);
  const [codeFieldErrorMessage, setCodeFieldErrorMessage] =
    useState<string>("");
  const [nameFieldError, setNameFieldError] = useState<boolean>(false);
  const [nameFieldErrorMessage, setNameFieldErrorMessage] =
    useState<string>("");

  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [items, setItems] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);

  const validateEquipmentFields = (): boolean => {
    if (!equipmentCode || equipmentCode.length === 0) {
      setAlertMessage("Equipment Code is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    setCodeFieldError(false);
    setCodeFieldErrorMessage("");
    if (
      !validateString(
        equipmentCode,
        validationLength.equipmentCode,
        validationType.CODE,
      )
    ) {
      setCodeFieldError(true);
      setCodeFieldErrorMessage(validationMessages.equipmentCode);
      return false;
    }

    if (!equipmentName || equipmentName.length === 0) {
      setAlertMessage("Equipment Description is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    setNameFieldError(false);
    setNameFieldErrorMessage("");
    if (
      !validateString(
        equipmentName,
        validationLength.equipmentName,
        validationType.NAME,
      )
    ) {
      setNameFieldError(true);
      setNameFieldErrorMessage(validationMessages.equipmentName);
      return false;
    }

    const listForDuplicate = selectedId
      ? items.filter((i: any) => String(i.id) !== String(selectedId))
      : items;
    if (
      checkDuplicate(
        listForDuplicate,
        DNDFieldsEnum.EQUIPMENTCODE,
        equipmentCode,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    return true;
  };

  useEffect(() => {
    fieldValues && setItems(fieldValues);
  }, []);

  useEffect(() => {
    data(items);
    setSelectedId(checkData(items, selectedId) ? selectedId : null);
  }, [items]);

  const updateItem = () => {
    if (!validateEquipmentFields()) {
      return false;
    }
    setItems((prev) =>
      prev.map((item: any) =>
        item.id === selectedId ? { ...item, ...payload } : item,
      ),
    );
    editItem(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${SpaPropertyEnum.serviceEquipment} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const addItem = () => {
    if (!validateEquipmentFields()) {
      return false;
    }
    setItems((prev) =>
      updateData(
        prev?.length
          ? [...prev, { id: prev.length + 1, ...payload }]
          : [{ id: 1, ...payload }],
      ),
    );
    setLastUpdatedRecord(payload);
    editItem(null);
    setEditClicked(false);
  };

  const editItem = (list: any) => {
    list?.id && setSelectedId(list.id);
    if (list != null) {
      setEditClicked(true);
      setEquipmentCode(list["Equipment Code"] ?? null);
      setEquipmentName(
        list["Equipment Description"] ?? list["Equipment Name"] ?? null,
      );
      setQuantity(
        list["Quantity"] !== undefined && list["Quantity"] !== null
          ? Number(list["Quantity"])
          : "",
      );
    } else {
      setEquipmentCode(null);
      setEquipmentName(null);
      setQuantity("");
      setCodeFieldError(false);
      setCodeFieldErrorMessage("");
      setNameFieldError(false);
      setNameFieldErrorMessage("");
    }
  };

  const deleteItemById = (id: number) => {
    setItems(deleteItem(items, id));
  };

  const payload = {
    "Equipment Code": equipmentCode,
    "Equipment Description": equipmentName,
    Quantity: quantity === "" ? undefined : Number(quantity),
  };

  return (
    <Stack direction="column" gap="10px">
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          padding: "20px",
        }}
      >
        <Stack
          direction="row"
          width="100%"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6" sx={textStyle}>
            Service Equipment
            <Tooltip
              title={tooltipComponent(
                spaTooltipHeaders?.serviceEquipmentHeader ??
                  golfTooltipHeaders?.categoryGroup ??
                  "Service Equipment",
              )}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </Typography>
          <Stack direction="row" gap="10px">
            {editClicked && (
              <ClearButton
                click={() => {
                  editItem(null);
                  setSelectedId(null);
                }}
              />
            )}
            {lastUpdatedRecord && (
              <CopyButton click={() => editItem(lastUpdatedRecord)} />
            )}
          </Stack>
        </Stack>
        <Stack direction="column" width="100%" gap="20px" flexWrap="wrap">
          <Stack direction="column">
            <Stack direction="row">
              <FormLabel>Equipment Code</FormLabel>
              <Tooltip
                title={tooltipComponent(
                  spaTooltipHeaders.serviceEquipmentEquipmentCode,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
            <TextField
              variant="outlined"
              placeholder="Equipment Code"
              value={equipmentCode ?? ""}
              error={codeFieldError}
              helperText={codeFieldError ? codeFieldErrorMessage : ""}
              onChange={(e) => setEquipmentCode(e.target.value || null)}
              fullWidth
            />
          </Stack>
          <Stack direction="column">
            <Stack direction="row">
              <FormLabel>Equipment Description</FormLabel>
              <Tooltip
                title={tooltipComponent(
                  spaTooltipHeaders.serviceEquipmentEquipmentDescription,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
            <TextField
              variant="outlined"
              placeholder="Equipment Description"
              value={equipmentName ?? ""}
              onChange={(e) => setEquipmentName(e.target.value || null)}
              fullWidth
              helperText={nameFieldError ? nameFieldErrorMessage : ""}
              error={nameFieldError}
            />
          </Stack>
          <Stack direction="column">
            <Stack direction="row">
              <FormLabel>Quantity</FormLabel>
              <Tooltip
                title={tooltipComponent(
                  spaTooltipHeaders.serviceEquipmentQuantity,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
            <TextField
              type="number"
              variant="outlined"
              placeholder="Quantity"
              value={quantity}
              onChange={(e) =>
                setQuantity(
                  e.target.value === ""
                    ? ""
                    : parseInt(e.target.value, 10) || "",
                )
              }
              fullWidth
            />
          </Stack>
        </Stack>
        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="contained"
            sx={{
              cursor: "pointer",
              border: "1px solid gray",
              borderRadius: "10px",
              background: "darkgreen",
              color: "white",
            }}
            onClick={() => (selectedId ? updateItem() : addItem())}
          >
            {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Equipment
          </Button>
        </Stack>
        <Stack direction="column" gap="10px" mt={4}>
          <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
            Do not use sample data. Those are for demo purposes only!
          </Typography>
          <Typography variant="h6">Service Equipment Available</Typography>
          {items ? (
            <DraggableItemList
              itemData={items}
              onOrderUpdate={(order) => setItems(order)}
              onEdit={editItem}
              onDelete={deleteItemById}
              code={DNDFieldsEnum.EQUIPMENTCODE}
              name={DNDFieldsEnum.EQUIPMENTNAME}
            />
          ) : (
            <Typography variant="body2">
              No Service Equipment Available
            </Typography>
          )}
        </Stack>
      </Paper>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default SpaServiceEquipment;
